'use no memo';

import { FormattedMessage, useIntl } from 'react-intl';
import {
  createFileRoute,
  getRouteApi,
  Outlet,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import BackIcon from '@mui/icons-material/ArrowBack';

import DrawerButton from '~/containers/DrawerButton';
import ContentView from '~/components/ContentView';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import Spacings from '~/modules/ui-kit/spacings';
import messages from '~/containers/UserProfile/messages';

const routeApi = getRouteApi('/user-profile');

const MasterPanel = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 'max-content',
  overflowY: 'auto',
  height: '100%',
  borderRight: `1px solid ${theme.palette.grey[300]}`,
}));

const DetailPanel = styled('div')({
  flex: 99999,
  overflowY: 'auto',
  height: '100%',
});

type SearchParams = {
  publisherId?: string;
  from?: string;
};

function Navigation() {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { publisherId, from } = routeApi.useSearch();
  const { data: participationSettings } =
    useSettingsByCategory('participation');

  const isActive = (path: string) => {
    return matchRoute({ to: path, fuzzy: false });
  };

  const navigateTo = (path: string) => {
    navigate({ to: path, search: { publisherId, from } });
  };

  return (
    <List>
      <ListItemButton
        selected={Boolean(isActive('/user-profile/account'))}
        onClick={() => navigateTo('/user-profile/account')}
      >
        <ListItemText primary={<FormattedMessage {...messages.account} />} />
      </ListItemButton>
      <ListItemButton
        selected={Boolean(isActive('/user-profile/settings'))}
        onClick={() => navigateTo('/user-profile/settings')}
      >
        <ListItemText primary={<FormattedMessage {...messages.settings} />} />
      </ListItemButton>
      {participationSettings?.allowPublishersToMaintainRoutePreferences ? (
        <ListItemButton
          selected={Boolean(isActive('/user-profile/route-preferences'))}
          onClick={() => navigateTo('/user-profile/route-preferences')}
        >
          <ListItemText
            primary={<FormattedMessage {...messages.routePreferences} />}
          />
        </ListItemButton>
      ) : null}
      <ListItemButton
        selected={Boolean(isActive('/user-profile/calendar-subscription'))}
        onClick={() => navigateTo('/user-profile/calendar-subscription')}
      >
        <ListItemText
          primary={<FormattedMessage {...messages.calendarSubscription} />}
        />
      </ListItemButton>
    </List>
  );
}

function UserProfileLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { from, publisherId } = routeApi.useSearch();
  const intl = useIntl();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const matchRoute = useMatchRoute();
  const isIndexRoute = matchRoute({ to: '/user-profile', fuzzy: false });

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            {from ? (
              <IconButton
                color="inherit"
                onClick={() => navigate({ to: from })}
                aria-label={intl.formatMessage(messages.back)}
                size="large"
              >
                <BackIcon />
              </IconButton>
            ) : (
              <DrawerButton />
            )}
            <Spacings.Stack scale="m">
              <Typography variant="h6" sx={{ ml: 2 }}>
                <FormattedMessage {...messages.header} />
              </Typography>
            </Spacings.Stack>
          </Toolbar>
        </AppBar>
      }
    >
      <Box sx={{ height: '100%', display: 'flex' }}>
        {(isDesktop || isIndexRoute) && (
          <MasterPanel>
            <Navigation />
          </MasterPanel>
        )}
        {(!isIndexRoute || isDesktop) && (
          <DetailPanel>
            <Spacings.Inset>
              <Spacings.Stack scale="m">
                {!isDesktop && !isIndexRoute ? (
                  <Button
                    variant="text"
                    startIcon={<BackIcon />}
                    onClick={() =>
                      navigate({
                        to: '/user-profile',
                        search: { publisherId, from },
                      })
                    }
                  >
                    <FormattedMessage {...messages.backToMenu} />
                  </Button>
                ) : null}
                <Outlet />
              </Spacings.Stack>
            </Spacings.Inset>
          </DetailPanel>
        )}
      </Box>
    </ContentView>
  );
}

export const Route = createFileRoute('/user-profile')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    publisherId: search.publisherId as string | undefined,
    from: search.from as string | undefined,
  }),
  component: UserProfileLayout,
});
